<template>
  <div>
    <TieredMenu
      ref="rowMenu"
      id="merchant_row_actions_menu"
      :model="rowMenuItems"
      :popup="true"
      class="text-sm"
      :key="rowMenuRowSelected"
    />

    <DataTable
      :value="displayData"
      :paginator="!loading"
      :rows="20"
      :rowsPerPageOptions="[10, 20, 50, 100]"
      :sortable="!loading"
      :loading="false"
      :globalFilterFields="[
        'MERCHANT_ID',
        'MERCHANT_NAME',
        'MERCHANT_OWNER',
        'PLATFORM',
        'TOLERANCE_ID',
      ]"
      class="p-datatable-sm"
      stripedRows
      hover
      responsiveLayout="scroll"
      scrollable
      scrollHeight="calc(100vh - 32rem)"
      data-test-id="fcc-merchants-table"
    >
      <Column
        field="MERCHANT_NAME"
        header="Supplier"
        sortable
        :pt="{
          bodyCell: [
            'hover:bg-surface-100/40 dark:hover:bg-surface-800/20 hover:shadow z-50 !p-0 group',
          ],
        }"
        style="width: 18rem"
      >
        <template #body="{ data }">
          <div
            v-if="loading"
            :data-test-id="`merchant-row-${data.MERCHANT_ID}`"
          >
            <Skeleton width="8rem" height="1rem" class="mb-2"></Skeleton>
            <Skeleton width="4rem" height="0.75rem"></Skeleton>
          </div>
          <div
            v-else
            :data-test-id="`merchant-row-${data.MERCHANT_ID}`"
          >
            <RouterLink
              :to="{
                name: 'fccMerchantDetails',
                params: { id: data.MERCHANT_ID },
              }"
              class="text-xs text-surface-700 dark:text-surface-200 active:opacity-80 group-hover:text-rch-black dark:group-hover:text-surface-300 inline-block p-4 -my-4 w-full"
              :data-test-id="`merchant-link-${data.MERCHANT_ID}`"
            >
              <div class="flex items-center gap-1 w-full h-full">
                <div class="flex flex-col">
                  <span class="text-sm font-medium">{{
                    data.MERCHANT_NAME
                  }}</span>
                  <span class="text-xs text-surface-500 dark:text-surface-400">{{
                    data.MERCHANT_ID
                  }}</span>
                </div>
                <Transition name="fade">
                  <ArrowTopRightOnSquareIcon
                    class="size-4 opacity-0 group-hover:opacity-100 ml-auto"
                  />
                </Transition>
              </div>
            </RouterLink>
          </div>
        </template>
      </Column>

      <Column field="MERCHANT_OWNER" header="Owner" sortable>
        <template #body="{ data }">
          <div v-if="loading">
            <Skeleton width="6rem" height="1rem"></Skeleton>
          </div>
          <div v-else class="text-sm text-surface-700 dark:text-surface-200 text-center w-fit mx-auto">
            {{ data.MERCHANT_OWNER }}
          </div>
        </template>
      </Column>

      <Column field="GO_LIVE_DATE" header="Go Live Date" sortable>
        <template #body="{ data }">
          <div v-if="loading">
            <Skeleton width="8rem" height="1rem"></Skeleton>
          </div>
          <div v-else class="text-sm text-surface-500 dark:text-surface-400 text-center w-fit mx-auto">
            {{ formatDate(data.GO_LIVE_DATE) }}
          </div>
        </template>
      </Column>

      <Column field="PLATFORM" header="Platform" sortable>
        <template #body="{ data }">
          <div v-if="loading">
            <Skeleton width="5rem" height="1rem"></Skeleton>
          </div>
          <div v-else class="text-sm text-surface-700 dark:text-surface-200 text-center w-fit mx-auto">
            {{ formatPlatform(data.PLATFORM) }}
          </div>
        </template>
      </Column>

      <Column field="TOLERANCE_ID" header="Tolerance" sortable>
        <template #body="{ data }">
          <div v-if="loading">
            <Skeleton
              width="5rem"
              height="1.5rem"
              borderRadius="12px"
            ></Skeleton>
          </div>
          <div v-else class="text-center w-fit mx-auto">
            <BadgeFccSeverity
              :value="formatTolerance(data.TOLERANCE_ID)"
              :background-color="
                getToleranceColor(data.TOLERANCE_ID).backgroundColor
              "
              :text-color="getToleranceColor(data.TOLERANCE_ID).textColor"
              :border-color="getToleranceColor(data.TOLERANCE_ID).borderColor"
            />
          </div>
        </template>
      </Column>

      <Column
        header="Actions"
        :exportable="false"
        style="width: 4rem"
        class="text-sm font-medium text-surface-600 dark:text-surface-300 border-l border-surface-200 dark:border-surface-700 text-center"
        frozen
        alignFrozen="right"
      >
        <template #body="{ data }">
          <div v-if="loading" class="flex justify-center items-center">
            <Skeleton shape="circle" size="2rem"></Skeleton>
          </div>
          <div v-else class="flex justify-center items-center">
            <Button
              icon="pi pi-ellipsis-v"
              rounded
              variant="outlined"
              aria-label="Row Actions"
              @click.prevent="toggleRowMenu($event, data)"
              :data-test-id="`merchant-actions-btn-${data.MERCHANT_ID}`"
            />
          </div>
        </template>
      </Column>
    </DataTable>

    <!-- Edit Tolerance Modal -->
    <EditToleranceModal v-if="canEdit" />
  </div>
</template>

<script setup lang="ts">
import { ref, nextTick, computed } from 'vue'
import { storeToRefs } from 'pinia'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Button from 'primevue/button'
import Skeleton from 'primevue/skeleton'
import TieredMenu from 'primevue/tieredmenu'
import { useFCCMerchantStore } from '@/stores/fcc'
import { useRouter } from 'vue-router'
import { Merchant } from '@/types/fcc'
import { useFCCFormatting } from '@/util/fcc/useFCCFormatting'
import BadgeFccSeverity from '@/components/common/badges/BadgeFccSeverity.vue'
import EditToleranceModal from '@/components/fcc/merchantdetails/EditToleranceModal.vue'
import { ArrowTopRightOnSquareIcon } from '@heroicons/vue/24/outline'
import { usePerms } from '@/composables/usePerms'
import { PERMISSIONS } from '@/config/permissions'

const merchantStore = useFCCMerchantStore()
const { filteredMerchants, loading } = storeToRefs(merchantStore)
const router = useRouter()
const { hasPerms } = usePerms()

const { formatDate, formatPlatform, formatTolerance, getToleranceColor } =
  useFCCFormatting()

const canEdit = computed(() => hasPerms(PERMISSIONS.FCC_UPDATE))

// Menu and dialog state
const rowMenu = ref(null)
const rowMenuItems = ref<any[]>([])
const rowMenuRowSelected = ref<PropertyKey | undefined>(undefined)

// Mock data for skeleton loading
const createMockMerchant = (index: number): Merchant => ({
  MERCHANT_ID: index,
  MERCHANT_NAME: '',
  MERCHANT_OWNER: '',
  GO_LIVE_DATE: '',
  PLATFORM: '',
  TOLERANCE_ID: '',
  MCC: '',
  KEY_METRICS: [],
})

const skeletonData = Array.from({ length: 25 }, (_, index) =>
  createMockMerchant(index),
)

// Computed property to return skeleton data when loading, real data otherwise
const displayData = computed(() => {
  return loading.value ? skeletonData : filteredMerchants.value
})


async function toggleRowMenu(event: Event, rowData: Merchant) {
  // Don't show menu for skeleton data
  if (loading.value) return

  rowMenuRowSelected.value = rowData.MERCHANT_ID ?? undefined
  rowMenuItems.value = buildRowMenuItems(rowData)
  await nextTick()
  if (rowMenu.value) {
    ;(rowMenu.value as any).toggle(event)
  }
}

function buildRowMenuItems(rowData: Merchant) {
  const items: any[] = []

  items.push({
    label: 'View Supplier Details',
    icon: 'pi pi-eye',
    command: () => viewMerchantDetails(rowData),
  })

  if (canEdit.value) {
    items.push({
      label: 'Modify Tolerance',
      icon: 'pi pi-cog',
      command: () => openToleranceModal(rowData),
    })
  }

  return items
}

function viewMerchantDetails(merchant: Merchant) {
  merchantStore.setCurrentMerchantId(merchant.MERCHANT_ID)
  merchantStore.setShowMerchantDetails(true)
  router.push({
    name: 'fccMerchantDetails',
    params: { id: merchant.MERCHANT_ID },
  })
}

function openToleranceModal(merchant: Merchant) {
  merchantStore.setCurrentMerchantId(merchant.MERCHANT_ID)
  merchantStore.setMerchantEventTolerance(merchant.TOLERANCE_ID)
  merchantStore.setIsEditModalOpen(true)
}
</script>
